import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Copy, Gift, UserPlus, Sparkles } from "lucide-react";

export default function ReferralsCard() {
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  const [code, setCode] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/referrals/me");
      setData(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, []);

  const copyLink = () => {
    if (!data?.referral_code) return;
    const link = `${window.location.origin}/?ref=${data.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const copyCode = () => {
    if (!data?.referral_code) return;
    navigator.clipboard.writeText(data.referral_code);
    toast.success("Code copied!");
  };

  const applyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      await api.post("/referrals/apply", { code: code.trim().toUpperCase() });
      toast.success("Referral applied! You'll both earn bonuses on your next paid entry.");
      setCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply code");
    } finally {
      setApplying(false);
    }
  };

  if (!data) return null;

  return (
    <div className="border border-white/10 bg-[#141414] p-6" data-testid="referrals-card">
      <div className="flex items-center gap-2 mb-1">
        <Gift size={16} className="text-[#00FF66]" />
        <div className="text-[#00FF66] text-xs uppercase tracking-widest">Referrals</div>
      </div>
      <h2 className="font-display text-3xl mb-1">INVITE & EARN</h2>
      <p className="text-white/50 text-sm mb-4">
        Both of you get <span className="text-[#00FF66] font-semibold">+{data.xp_bonus} XP</span> and{" "}
        <span className="text-[#00FF66] font-semibold">${data.credit_bonus.toFixed(2)} credit</span>{" "}
        when your friend makes their first paid entry.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Your code */}
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-2">Your referral code</div>
          <div className="flex gap-2 w-full">
            <div className="flex-1 min-w-0 bg-[#0a0a0a] border border-white/10 px-3 py-2.5 font-display text-xl sm:text-2xl tracking-[0.15em] text-center truncate">
              {data.referral_code}
            </div>
            <button
              onClick={copyCode}
              className="flex-shrink-0 p-2.5 border border-white/10 hover:border-white/30 rounded-sm"
              title="Copy code"
              data-testid="copy-referral-code"
            >
              <Copy size={16} />
            </button>
          </div>
          <button
            onClick={copyLink}
            className="w-full mt-2 py-2 border border-[#00FF66]/40 hover:bg-[#00FF66]/10 text-[#00FF66] text-xs uppercase tracking-widest font-bold rounded-sm inline-flex items-center justify-center gap-2"
            data-testid="copy-referral-link"
          >
            <Copy size={12} /> Copy Invite Link
          </button>

          <div className="mt-4 grid grid-cols-2 gap-2 text-center">
            <div className="border border-white/10 p-2">
              <div className="font-display text-2xl text-[#00FF66]">{data.referrals_count}</div>
              <div className="text-white/40 text-xs uppercase tracking-widest">Referred</div>
            </div>
            <div className="border border-white/10 p-2">
              <div className="font-display text-2xl">
                {data.awards?.filter((a) => a.referrer_id).length || 0}
              </div>
              <div className="text-white/40 text-xs uppercase tracking-widest">Bonuses</div>
            </div>
          </div>
        </div>

        {/* Apply a code */}
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-2">Got a code?</div>
          {data.referred_by ? (
            <div className="border border-white/10 bg-[#0a0a0a] p-4 h-full flex flex-col items-center justify-center text-center">
              <Sparkles size={20} className="text-[#00FF66] mb-2" />
              <div className="font-display text-lg mb-1">REFERRAL APPLIED</div>
              <p className="text-white/50 text-xs">
                {data.referral_bonus_awarded
                  ? "Bonus received on your first paid entry."
                  : "Bonus unlocks on your first paid entry."}
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-2 w-full">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="6-CHAR CODE"
                  maxLength={6}
                  data-testid="apply-code-input"
                  className="flex-1 min-w-0 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] px-3 py-2.5 font-display text-xl sm:text-2xl tracking-[0.15em] text-center outline-none"
                />
                <button
                  onClick={applyCode}
                  disabled={applying || code.length < 4}
                  data-testid="apply-code-btn"
                  className="flex-shrink-0 px-3 sm:px-4 bg-[#00FF66] hover:bg-[#00e65b] disabled:bg-white/10 disabled:text-white/30 text-black font-bold uppercase tracking-widest text-xs rounded-sm inline-flex items-center gap-1 whitespace-nowrap"
                >
                  <UserPlus size={14} />
                  Apply
                </button>
              </div>
              <p className="text-white/40 text-xs mt-2">
                Applies once. Both parties earn on your first paid contest entry.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
